import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { ChevronDown, Loader2, Play } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'

export type NodeLayoutVariant = 'default' | 'gen' | 'media' | 'compact' | 'output'

/** 未选中时显示在节点虚线框外的左上角标题 */
export function NodeExternalLabel({ title }: { title: string }) {
  return (
    <span className="cn-node-external-label" title={title}>
      {title}
    </span>
  )
}

/** 节点外框：承载虚线边框外的标题 + 节点壳 */
export function NodeFrame({
  width,
  fullWidth,
  selected,
  accentColor,
  categoryColor,
  title,
  children,
}: {
  width: number
  fullWidth?: boolean
  selected?: boolean
  accentColor: string
  categoryColor: string
  title: string
  children: ReactNode
}) {
  return (
    <div
      className="cn-node-frame"
      style={
        {
          width: fullWidth ? '100%' : width,
          '--cn-accent': accentColor,
          '--cn-category': categoryColor,
        } as React.CSSProperties
      }
    >
      {!selected ? <NodeExternalLabel title={title} /> : null}
      {children}
    </div>
  )
}

/** 节点外壳 — 视觉由 data-node-theme 驱动 */
export function NodeShell({
  width,
  selected,
  running,
  accentColor,
  categoryColor,
  layout = 'default',
  className,
  children,
}: {
  width: number
  selected?: boolean
  running?: boolean
  accentColor: string
  categoryColor: string
  layout?: NodeLayoutVariant
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={clsx(
        'cn-node',
        `cn-node--${layout}`,
        selected && 'cn-node--selected cn-shell--selected',
        running && 'cn-node--running',
        className,
      )}
      style={
        {
          width,
          '--cn-accent': accentColor,
          '--cn-category': categoryColor,
        } as React.CSSProperties
      }
    >
      {children}
    </div>
  )
}

export function NodeHeader({
  categoryLabel,
  categoryColor,
  title,
  status,
  statusLabel,
  runButton,
}: {
  categoryLabel: string
  categoryColor: string
  title: string
  accentColor: string
  status: 'idle' | 'pending' | 'running' | 'succeeded' | 'failed'
  statusLabel?: string
  runButton?: { onClick: () => void; disabled?: boolean; label: string; running?: boolean }
}) {
  const statusClass =
    status === 'running' || status === 'pending'
      ? 'cn-status--running'
      : status === 'succeeded'
        ? 'cn-status--succeeded'
        : status === 'failed'
          ? 'cn-status--failed'
          : 'cn-status--idle'

  return (
    <header className="cn-head cn-node-chrome">
      <div className="cn-head__main">
        <span className="cn-head__stripe" style={{ background: categoryColor }} aria-hidden />
        <div className="cn-head__text">
          <span className="cn-head__cat" style={{ color: categoryColor }}>
            {categoryLabel}
          </span>
          <span className="cn-head__title">{title}</span>
        </div>
      </div>
      <div className="cn-head__actions">
        {statusLabel ? (
          <span className={clsx('cn-status-pill', statusClass)}>{statusLabel}</span>
        ) : (
          <span className={clsx('cn-status', statusClass)} title={status} />
        )}
        {runButton ? (
          <Button
            type="button"
            variant="default"
            size="sm"
            disabled={runButton.disabled}
            onClick={runButton.onClick}
            className="cn-run cn-run--primary"
          >
            {runButton.running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            <span>{runButton.label}</span>
          </Button>
        ) : null}
      </div>
    </header>
  )
}

/** 主体内容区：扁平单层；scroll 时隐藏滚动条但仍可滚动 */
export function NodeBody({
  children,
  flush,
  scroll,
}: {
  children: ReactNode
  flush?: boolean
  scroll?: boolean
}) {
  const scrollable = scroll ?? !flush
  return (
    <div
      className={clsx(
        'cn-body',
        flush && 'cn-body--flush',
        scrollable && 'cn-body--scroll studio-scrollbar-hidden',
      )}
    >
      {children}
    </div>
  )
}

export function NodeFooter({ children }: { children: ReactNode }) {
  return <footer className="cn-foot cn-node-chrome">{children}</footer>
}

/** 预览 / 输出主视觉区 */
export function NodeHero({ children, className, empty }: { children?: ReactNode; className?: string; empty?: string }) {
  return (
    <div className={clsx('cn-hero', className)}>
      {children ?? (empty ? <span className="cn-hero__empty">{empty}</span> : null)}
    </div>
  )
}

/** 分节：仅标题分隔线，不再套嵌套容器 */
export function NodeZone({
  label,
  children,
  compact,
}: {
  label?: string
  children: ReactNode
  compact?: boolean
}) {
  return (
    <section className={clsx('cn-zone', compact && 'cn-zone--compact')}>
      {label ? <h4 className="cn-zone__label">{label}</h4> : null}
      {children}
    </section>
  )
}

/** 可折叠高级区 */
export function NodeCollapsible({
  label,
  defaultOpen,
  children,
}: {
  label: string
  defaultOpen?: boolean
  children: ReactNode
}) {
  return (
    <details className="cn-fold" open={defaultOpen}>
      <summary className="cn-fold__summary">
        <ChevronDown className="cn-fold__chevron h-3.5 w-3.5" />
        {label}
      </summary>
      <div className="cn-fold__body">{children}</div>
    </details>
  )
}

/** 表单行：标签 + 控件 */
export function NodeField({
  label,
  children,
  span = 1,
}: {
  label: string
  children: ReactNode
  span?: 1 | 2
}) {
  return (
    <div className={clsx('cn-field-row', span === 2 && 'cn-field-row--wide')}>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  )
}

/** 参数网格 */
export function NodeParamGrid({ children }: { children: ReactNode }) {
  return <div className="cn-param-grid">{children}</div>
}

export function FieldLabel({ children }: { children: ReactNode }) {
  return <span className="cn-label">{children}</span>
}

export function TechField({
  multiline,
  rows = 3,
  className,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  multiline?: boolean
  rows?: number
}) {
  if (multiline) {
    return <textarea rows={rows} className={clsx('cn-field', className)} {...props} />
  }
  return <input className={clsx('cn-field', className)} {...(props as React.InputHTMLAttributes<HTMLInputElement>)} />
}

const CANVAS_INTERACTIVE = 'nodrag nopan nowheel'

function stopCanvasPointer(e: React.SyntheticEvent) {
  e.stopPropagation()
}

/** 画布外置面板专用：本地草稿 + 防抖/失焦提交，避免每键触发全图重渲染；兼容中文 IME */
export function CanvasDockTextarea({
  value,
  onCommit,
  className,
  debounceMs = 400,
  liveRef,
  onFocus,
  onBlur,
  onCompositionStart,
  onCompositionEnd,
  onChange,
  ...props
}: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> & {
  value: string
  onCommit: (value: string) => void
  debounceMs?: number
  /** 始终指向当前草稿，供「生成」等操作读取未防抖提交的内容 */
  liveRef?: React.MutableRefObject<string>
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>
}) {
  const [draft, setDraft] = useState(value)
  const composingRef = useRef(false)
  const focusedRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(value)
      if (liveRef) liveRef.current = value
    }
  }, [value, liveRef])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const scheduleCommit = useCallback(
    (next: string) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => onCommit(next), debounceMs)
    },
    [onCommit, debounceMs],
  )

  const flushCommit = useCallback(
    (next: string) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      onCommit(next)
    },
    [onCommit],
  )

  return (
    <textarea
      {...props}
      value={draft}
      className={clsx('cn-field', CANVAS_INTERACTIVE, className)}
      onPointerDown={stopCanvasPointer}
      onMouseDown={stopCanvasPointer}
      onTouchStart={stopCanvasPointer}
      onDoubleClick={stopCanvasPointer}
      onWheel={stopCanvasPointer}
      onFocus={(e) => {
        focusedRef.current = true
        onFocus?.(e)
      }}
      onBlur={(e) => {
        focusedRef.current = false
        flushCommit(e.currentTarget.value)
        onBlur?.(e)
      }}
      onCompositionStart={(e) => {
        composingRef.current = true
        onCompositionStart?.(e)
      }}
      onCompositionEnd={(e) => {
        composingRef.current = false
        const next = e.currentTarget.value
        setDraft(next)
        if (liveRef) liveRef.current = next
        scheduleCommit(next)
        onCompositionEnd?.(e)
      }}
      onChange={(e) => {
        const next = e.target.value
        setDraft(next)
        if (liveRef) liveRef.current = next
        if (!composingRef.current) scheduleCommit(next)
        onChange?.(e)
      }}
    />
  )
}

export function TechPreview({
  className,
  empty,
  children,
}: {
  className?: string
  empty?: string
  children?: ReactNode
}) {
  return (
    <div className={clsx('cn-preview', className)}>
      {children ?? (empty ? <span className="cn-preview__empty">{empty}</span> : null)}
    </div>
  )
}

/** @deprecated 使用 NodeZone */
export function InsetPanel({ label, children }: { label?: string; children: ReactNode }) {
  return <NodeZone label={label}>{children}</NodeZone>
}

export function NodeChipStrip({ children }: { children: ReactNode }) {
  return <div className="cn-chip-strip">{children}</div>
}

export function NodeChip({
  label,
  sublabel,
  color,
  onClick,
  thumbnail,
}: {
  label: string
  sublabel?: string
  color?: string
  onClick?: () => void
  thumbnail?: ReactNode
}) {
  const Tag = onClick ? 'button' : 'span'
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className="cn-chip"
      style={color ? { '--chip-accent': color } as React.CSSProperties : undefined}
      title={label}
    >
      <span className="cn-chip__thumb">{thumbnail ?? '·'}</span>
      <span className="cn-chip__text">
        <span className="cn-chip__label">{label}</span>
        {sublabel ? <span className="cn-chip__sub">{sublabel}</span> : null}
      </span>
    </Tag>
  )
}
